/**
 * useContacts Hook
 * ahooks data fetching hook for vendor contacts
 */

import { useRequest } from 'ahooks';
import type { Options } from 'ahooks/lib/useRequest/src/types';
import { contactApi } from '@/services/api';
import { useContactStore, useContactsForVendor, usePaginationForVendor } from '@/stores';
import type { ContactListResponse, ContactListParams, ContactDetailResponse } from '@/types/api';
import { getStoreFirstArray, getStoreFirstValue, getCacheKey } from '@/utils/storeFirstData';
import { trackCacheKey, clearCacheWhere } from '@/utils/cacheKeyRegistry';

/**
 * Helper to invalidate all contact caches for a vendor
 */
const invalidateContactCaches = (vendorId: number) => {
  // Exact-key clear of the tracked contact keys (the old predicate form was a
  // silent no-op; see cacheKeyRegistry.ts)
  clearCacheWhere((key) => key.includes(`/api/vendors/${vendorId}/contacts`));
};

/**
 * Hook to fetch contacts for a specific vendor
 */
export function useContacts(
  vendorId: number | null,
  params?: Omit<ContactListParams, 'vendorId'>,
  config?: Options<ContactListResponse, []>
) {
  const { setContactsForVendor, setPaginationForVendor, setLoading, setError } = useContactStore();

  // Fetch contacts with ahooks
  const { data, error, loading, refresh } = useRequest(() => contactApi.list(vendorId!, params), {
    ready: !!vendorId,
    refreshDeps: [vendorId, JSON.stringify(params)],
    cacheKey: vendorId
      ? trackCacheKey(getCacheKey(`/api/vendors/${vendorId}/contacts`, params))
      : undefined,
    staleTime: 5000, // 5 seconds - reduced for better responsiveness after mutations
    ...config,
    onSuccess: (data) => {
      if (vendorId) {
        setContactsForVendor(vendorId, data.data);
        setPaginationForVendor(vendorId, data.pagination);
        setLoading(false);
        setError(null);
      }
      config?.onSuccess?.(data, []);
    },
    onError: (err) => {
      setLoading(false);
      setError(err.message || 'Failed to fetch contacts');
      config?.onError?.(err, []);
    },
  });

  // Get contacts from store for immediate access
  // Always call hooks unconditionally (React Rules of Hooks)
  // Use 0 as fallback when vendorId is null (will return empty array from store)
  const contactsFromStore = useContactsForVendor(vendorId ?? 0);
  const paginationFromStore = usePaginationForVendor(vendorId ?? 0);

  const contacts = vendorId ? contactsFromStore : [];
  const pagination = vendorId ? paginationFromStore : undefined;

  // Store-first pattern: prioritize store data for instant UI updates after mutations
  return {
    contacts: getStoreFirstArray(contacts, data?.data),
    pagination: getStoreFirstValue(pagination, data?.pagination),
    error,
    isLoading: loading,
    mutate: refresh,
    refresh,
  };
}

/**
 * Hook to fetch a single contact by ID
 * Used for edit modal to get raw address data
 */
export function useContactDetail(
  vendorId: number | null,
  contactId: number | null,
  config?: Options<ContactDetailResponse, []>
) {
  const { data, error, loading, refresh } = useRequest(
    () => contactApi.getById(vendorId!, contactId!),
    {
      ready: !!vendorId && !!contactId,
      refreshDeps: [vendorId, contactId],
      cacheKey:
        vendorId && contactId
          ? trackCacheKey(`/api/vendors/${vendorId}/contacts/${contactId}`)
          : undefined,
      staleTime: 5000, // 5 seconds
      ...config,
    }
  );

  return {
    contact: data ?? null,
    error,
    isLoading: loading,
    refresh,
  };
}

/**
 * Hook for contact mutations (create, update, toggle status)
 * All mutations invalidate ahooks cache to ensure UI stays in sync
 */
export function useContactMutations(vendorId: number) {
  const { addContact, updateContact: updateContactInStore } = useContactStore();

  const createContact = async (data: Parameters<typeof contactApi.create>[1]) => {
    const response = await contactApi.create(vendorId, data);
    addContact(vendorId, response);
    // Invalidate cache to refresh list
    invalidateContactCaches(vendorId);
    return response;
  };

  const updateContact = async (
    contactId: number,
    data: Parameters<typeof contactApi.update>[2]
  ) => {
    const response = await contactApi.update(vendorId, contactId, data);
    updateContactInStore(vendorId, contactId, response);
    // Invalidate cache to refresh list
    invalidateContactCaches(vendorId);
    return response;
  };

  const toggleContactStatus = async (contactId: number, isActive: boolean) => {
    const response = await contactApi.toggleStatus(vendorId, contactId, isActive);
    // Update store with full contact response
    updateContactInStore(vendorId, contactId, response);
    // Invalidate cache to refresh list with updated status
    invalidateContactCaches(vendorId);
    return response;
  };

  const setPrimaryContact = async (contactId: number, currentPrimaryId?: number) => {
    // Use the new set-primary endpoint
    // Backend automatically handles unsetting previous primary
    const response = await contactApi.setPrimary(vendorId, contactId, true);

    // Update store for the new primary contact
    updateContactInStore(vendorId, contactId, { isPrimary: true });

    // Update store for the previous primary contact if exists
    if (currentPrimaryId && currentPrimaryId !== contactId) {
      updateContactInStore(vendorId, currentPrimaryId, { isPrimary: false });
    }

    // Invalidate cache to refresh list with updated primary
    invalidateContactCaches(vendorId);
    return response;
  };

  return {
    createContact,
    updateContact,
    toggleStatus: toggleContactStatus,
    setPrimary: setPrimaryContact,
  };
}

/**
 * Hook to manage contact modal state
 */
export function useContactModal(vendorId: number) {
  const {
    isContactModalOpen,
    currentVendorId,
    editingContactId,
    openContactModal,
    closeContactModal,
  } = useContactStore();

  const isOpen = isContactModalOpen && currentVendorId === vendorId;

  const openModal = () => {
    openContactModal(vendorId);
  };

  const openEditModal = (contactId: number) => {
    openContactModal(vendorId, contactId);
  };

  const closeModal = () => {
    closeContactModal();
  };

  return {
    isOpen,
    editingContactId,
    openModal,
    openEditModal,
    closeModal,
    isEditMode: !!editingContactId,
  };
}
